/**
 * Slate radius effects (ADR 0008). Slates are socketed into rocks and emit
 * deterministic passives; like attack-speed auras, effects never stack --
 * the best value per effect wins. No RNG, no state: pure functions of the
 * grid, so boards without slates (every pre-v3 replay) are unaffected.
 */
import { CELL, SLATES, type SlateKey } from '../data/balance.js';
import { NX, NY } from '../data/balance.js';
import { dist } from './dist.js';
import type { Cell, GemCell } from './types.js';

export interface SlateInstance {
  readonly x: number;
  readonly y: number;
  readonly key: SlateKey;
}

/** Every slate on the board, in row-major order. */
export function collectSlates(grid: readonly (readonly (Cell | null)[])[]): SlateInstance[] {
  const out: SlateInstance[] = [];
  for (let y = 0; y < NY; y++) {
    for (let x = 0; x < NX; x++) {
      const c = grid[y]![x];
      if (c && c.kind === 'slate') out.push({ x, y, key: c.key });
    }
  }
  return out;
}

/** Gem-side buffs (Lodestone/Whetstone/Beacon families); null when unbuffed. */
export interface GemSlateBuffs {
  readonly vsAir: number;
  readonly shred: number;
  readonly rangeBonus: number;
}

export function gemSlateBuffs(
  slates: readonly SlateInstance[],
  cell: GemCell,
): GemSlateBuffs | null {
  let vsAir = 1;
  let shred = 0;
  let rangeBonus = 0;
  for (const s of slates) {
    const def = SLATES[s.key];
    if (def.vsAir === undefined && def.shred === undefined && def.rangeBonus === undefined) {
      continue;
    }
    if (dist(s.x - cell.x, s.y - cell.y) > def.radius) continue;
    if (def.vsAir !== undefined && def.vsAir > vsAir) vsAir = def.vsAir;
    if (def.shred !== undefined && def.shred > shred) shred = def.shred;
    if (def.rangeBonus !== undefined && def.rangeBonus > rangeBonus) rangeBonus = def.rangeBonus;
  }
  return vsAir > 1 || shred > 0 || rangeBonus > 0 ? { vsAir, shred, rangeBonus } : null;
}

/** Creep-side zone effects (Hourglass/Ember/Hollow families) at a pixel position. */
export interface CreepZone {
  readonly slowMult: number;
  readonly poisonMult: number;
  readonly dmgMult: number;
}

export function creepZone(
  slates: readonly SlateInstance[],
  px: number,
  py: number,
): CreepZone | null {
  let slowMult = 1;
  let poisonMult = 1;
  let dmgMult = 1;
  for (const s of slates) {
    const def = SLATES[s.key];
    if (def.zoneSlow === undefined && def.poisonRate === undefined && def.dmgTaken === undefined) {
      continue;
    }
    if (dist((s.x + 0.5) * CELL - px, (s.y + 0.5) * CELL - py) > def.radius * CELL) continue;
    if (def.zoneSlow !== undefined && def.zoneSlow < slowMult) slowMult = def.zoneSlow;
    if (def.poisonRate !== undefined && def.poisonRate > poisonMult) poisonMult = def.poisonRate;
    if (def.dmgTaken !== undefined && def.dmgTaken > dmgMult) dmgMult = def.dmgTaken;
  }
  return slowMult < 1 || poisonMult > 1 || dmgMult > 1 ? { slowMult, poisonMult, dmgMult } : null;
}

/** Best aura-rate multiplier (Prism family) covering a grid cell. */
export function prismMult(slates: readonly SlateInstance[], x: number, y: number): number {
  let best = 1;
  for (const s of slates) {
    const def = SLATES[s.key];
    if (def.auraMult === undefined || def.auraMult <= best) continue;
    if (dist(s.x - x, s.y - y) <= def.radius) best = def.auraMult;
  }
  return best;
}

/** Best spite fraction (Gravestone family) covering a death at pixel position. */
export function spiteFor(slates: readonly SlateInstance[], px: number, py: number): number {
  let best = 0;
  for (const s of slates) {
    const def = SLATES[s.key];
    if (def.spite === undefined || def.spite <= best) continue;
    if (dist((s.x + 0.5) * CELL - px, (s.y + 0.5) * CELL - py) <= def.radius * CELL) {
      best = def.spite;
    }
  }
  return best;
}
